
import React from 'react';
import { UserProfile, Client } from '@/shared/types';

interface TemplateProps {
  profile: UserProfile;
  client?: Client | null;
  title: string;
  metaData: { label: string; value: string }[];
  children: React.ReactNode;
  footerNote?: string;
  type: string;
  containerClass: string;
  LogoImage: React.FC<{ size: 'sm' | 'md' | 'lg' | 'xl'; className?: string; light?: boolean }>;
  hideHeader?: boolean;
}

// This template is exclusively for SwaySuite's own billing invoices sent to freelancers.
// It matches the platform branding and cannot be selected by users.
const SystemInvoiceTemplate: React.FC<TemplateProps> = ({
  profile, client, title, metaData, children, footerNote, type, containerClass, LogoImage, hideHeader = false
}) => {
  return (
    <div className={containerClass}>
       <div className="flex flex-col h-full bg-neutral-50">
           {!hideHeader && (
             <>
               {/* Corporate Header */}
               <div className="bg-neutral-900 text-white p-12">
                   <div className="flex justify-between items-start mb-8">
                       <div className="flex items-center gap-4">
                           <LogoImage size="md" light className="bg-white/10 p-2 rounded-xl" />
                           <div>
                               <h1 className="text-2xl font-bold tracking-tight">{profile.name}</h1>
                               <p className="text-sm text-neutral-400">Freelancer Management Systems</p>
                           </div>
                       </div>
                       <div className="text-right">
                           <h2 className="text-3xl font-light text-white uppercase tracking-widest">{type}</h2>
                           <div className="mt-2 text-sm text-primary-300 font-mono">
                               {metaData.find(m => m.label.includes('Ref'))?.value}
                           </div>
                       </div>
                   </div>

                   <div className="grid grid-cols-2 gap-8 text-sm">
                       <div>
                           <p className="text-neutral-500 text-xs font-bold uppercase tracking-wider mb-2">From</p>
                           <p className="text-white font-medium">{profile.name}</p>
                           <p className="text-neutral-400">{profile.address}</p>
                           <p className="text-neutral-400">{profile.email}</p>
                       </div>
                       <div className="text-right">
                           <p className="text-neutral-500 text-xs font-bold uppercase tracking-wider mb-2">Billed To</p>
                           {client ? (
                               <>
                                   <p className="text-white font-medium text-lg">{client.name}</p>
                                   <p className="text-neutral-400">{client.email}</p>
                                   <p className="text-neutral-400">{client.company}</p>
                               </>
                           ) : <span>Valued Customer</span>}
                       </div>
                   </div>
               </div>

               {/* Date Bar */}
               <div className="bg-white border-b border-neutral-200 px-12 py-4 flex justify-between items-center text-sm">
                    {metaData.filter(m => !m.label.includes('Ref')).map((m, i) => (
                        <div key={i} className="flex gap-2">
                            <span className="font-bold text-neutral-500 uppercase">{m.label}:</span>
                            <span className="font-medium text-neutral-900">{m.value}</span>
                        </div>
                    ))}
               </div>
             </>
           )}

           {/* Content */}
           <div className="p-12 flex-1">
               <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
                   {children}
               </div>
           </div>

           {/* Footer */}
           <div className="p-12 text-center border-t border-neutral-200 bg-white">
               <p className="text-neutral-900 font-bold mb-2">Thank you for choosing {profile.name}.</p>
               <p className="text-sm text-neutral-500 max-w-lg mx-auto">{footerNote}</p>
               <div className="mt-6 text-xs text-neutral-400 uppercase tracking-widest">
                   Automated System Invoice • {new Date().getFullYear()}
               </div>
           </div>
       </div>
    </div>
  );
};

export default SystemInvoiceTemplate;
